import { useEffect, useMemo, useState } from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { alertApiRef, useApi } from '@backstage/frontend-plugin-api';
import { Link } from '@backstage/core-components';
import {
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CodeIcon from '@material-ui/icons/Code';
import { shortRef } from '../vocabulary/refs';
import DescriptionIcon from '@material-ui/icons/Description';
import LaunchIcon from '@material-ui/icons/Launch';

/*
 * Entity overview.
 *
 * The stock overview is a wall of equal-weight cards — an About card and a
 * relations graph competing for the same visual priority — so nothing answers
 * the questions people actually arrive with. Industry guidance for developer
 * portals is consistent that a service page must immediately answer:
 * who owns this, where are the docs/source, what is it connected to, and does
 * it meet our standards.
 *
 * So this uses a substance-left / metadata-right split (the GitHub repo and
 * Linear issue pattern):
 *   LEFT   summary -> readiness -> relationships (the reading path)
 *   RIGHT  details and links (scannable reference, never the focus)
 *
 * The relations *graph* is deliberately not here; it lives on its own tab.
 * Graphs are for exploring, lists are for answering.
 */

const useStyles = makeStyles(theme => ({
  section: { marginBottom: 26 },
  sectionTitle: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    marginBottom: 10,
  },
  panel: {
    border: '1px solid var(--ar-border-soft)',
    borderRadius: 8,
    background: 'var(--ar-surface)',
    padding: '14px 16px',
  },
  description: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: 'var(--ar-fg)',
  },
  muted: { color: 'var(--ar-fg-2)', fontSize: '0.875rem' },

  /* Readiness checklist */
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 0',
    fontSize: '0.875rem',
  },
  checkOn: { color: '#3fb950' },
  checkOff: { color: 'var(--ar-fg-3)' },
  checkLabelOff: { color: 'var(--ar-fg-2)' },

  /* Relationship lists */
  relRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid var(--ar-border-soft)',
  },
  relRowLast: { borderBottom: 'none' },
  relLabel: {
    minWidth: 104,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  relValues: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  // The table draws its own row rules, so the panel only supplies the border.
  relTable: { margin: '-14px -16px', width: 'calc(100% + 32px)' },
  relDim: { color: 'var(--ar-fg-2)' },

  /* Right sidebar */
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 16,
    padding: '11px 0',
    borderBottom: '1px solid var(--ar-border-soft)',
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  detailRowLast: { borderBottom: 'none' },
  // Label stays narrow and quiet so the column of values reads as one list.
  detailKey: {
    color: 'var(--ar-fg-3)',
    flex: '0 0 auto',
    whiteSpace: 'nowrap',
  },
  detailVal: {
    color: 'var(--ar-fg)',
    textAlign: 'right',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 0',
    fontSize: '0.875rem',
    color: theme.palette.primary.main,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    justifyContent: 'flex-end',
  },

  /* Section header with actions on the right */
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  actions: { display: 'flex', gap: 2 },
  actionBtn: { padding: 5, color: 'var(--ar-fg-3)' },
}));

/**
 * Route for an entity ref, from any of the three spellings the catalog accepts.
 *
 * `relations[].targetRef` is always the full `kind:namespace/name`, but
 * `spec.owner` and `spec.system` are whatever the YAML author typed — and most
 * catalog-info.yaml files in the org type the bare name. Splitting on ':' and
 * '/' by hand read the bare "guests" as its KIND and produced `/catalog//guests/`,
 * which matches no route, so every owner link written the short way landed on
 * the catalog index instead of the owner. Hence `parseEntityRef` with an
 * explicit defaultKind per field.
 *
 * A ref the catalog model will not parse should cost one dead link, not the
 * whole page, so the fallback is the browse page rather than a throw.
 */
const entityPath = (ref: string, defaultKind = 'component') => {
  try {
    const { kind, namespace, name } = parseEntityRef(ref, {
      defaultKind,
      defaultNamespace: 'default',
    });
    return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
  } catch {
    return '/browse';
  }
};

/** Relations we surface, in the order they answer questions. */
const REL_LABELS: Array<[string, string]> = [
  ['partOf', 'Part of'],
  ['hasPart', 'Contains'],
  ['dependsOn', 'Depends on'],
  ['dependencyOf', 'Used by'],
  ['providesApi', 'Provides API'],
  ['consumesApi', 'Consumes API'],
];

const Check = ({ ok, label }: { ok: boolean; label: string }) => {
  const classes = useStyles();
  return (
    <div className={classes.check}>
      {ok ? (
        <CheckCircleIcon fontSize="small" className={classes.checkOn} />
      ) : (
        <RadioButtonUncheckedIcon
          fontSize="small"
          className={classes.checkOff}
        />
      )}
      <span className={ok ? undefined : classes.checkLabelOff}>{label}</span>
    </div>
  );
};

export const EntityOverview = () => {
  const classes = useStyles();
  const { entity } = useEntity<Entity>();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const [refreshing, setRefreshing] = useState(false);

  // The stock About card carried refresh/edit; replacing it dropped them, which
  // matters most for Templates where re-scanning is part of the workflow.
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled — the entity will update shortly.',
        severity: 'success',
        display: 'transient',
      });
    } catch (e) {
      alertApi.post({
        message: `Could not schedule a refresh: ${(e as Error).message}`,
        severity: 'error',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const md = entity.metadata;
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const ann = md.annotations ?? {};

  const description = md.description ?? '';
  const sourceUrl = ann['backstage.io/source-location']?.replace(/^url:/, '');
  const hasDocs = Boolean(ann['backstage.io/techdocs-ref']);
  const editUrl = ann['backstage.io/edit-url'];
  const owner = typeof spec.owner === 'string' ? shortRef(spec.owner) : '';
  const system = typeof spec.system === 'string' ? shortRef(spec.system) : '';
  const tags = md.tags ?? [];
  const links = md.links ?? [];

  const relations = entity.relations ?? [];
  const grouped = REL_LABELS.map(([type, label]) => ({
    label,
    refs: relations.filter(r => r.type === type).map(r => r.targetRef),
  })).filter(g => g.refs.length > 0);

  // Flattened once so the table is one row per related entity rather than one
  // row per relation type with a wrapped list of links inside it.
  const relRows = useMemo(
    () => grouped.flatMap(g => g.refs.map(ref => ({ relation: g.label, ref }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grouped.map(g => `${g.label}:${g.refs.join(',')}`).join('|')],
  );
  const refsKey = relRows.map(r => r.ref).join('|');

  // The relations on the entity carry only refs. Kind, type and lifecycle have
  // to be looked up, and they are what make the table worth reading.
  const [related, setRelated] = useState<Map<string, Entity>>(new Map());
  useEffect(() => {
    const refs = refsKey ? refsKey.split('|') : [];
    if (refs.length === 0) {
      setRelated(new Map());
      return undefined;
    }
    let active = true;
    catalogApi
      .getEntitiesByRefs({
        entityRefs: refs,
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'spec.type',
          'spec.lifecycle',
        ],
      })
      .then(res => {
        if (!active) return;
        const m = new Map<string, Entity>();
        res.items.forEach((e, i) => {
          if (e) m.set(refs[i], e as Entity);
        });
        setRelated(m);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [catalogApi, refsKey]);

  // Standing in for a scorecard until real checks exist: the handful of
  // hygiene facts that make an entity actually useful to someone on call.
  //
  // There used to be a client check here ("Linked to a client" / "Has
  // deliverables"), keyed on `kind: System` long after D-04 had moved clients
  // to Domain — so it asked every component about a client that the System it
  // named was not. Clients left the portal altogether in D-33 (CustomerIQ owns
  // them), and the check went with them.
  const readiness = [
    { ok: Boolean(owner), label: 'Has an owning team' },
    { ok: Boolean(description), label: 'Has a description' },
    { ok: Boolean(sourceUrl), label: 'Source code linked' },
    { ok: hasDocs, label: 'Documentation published' },
  ];
  const met = readiness.filter(r => r.ok).length;

  // What the entity IS, then who is accountable for it. Ordered deliberately:
  // kind and type identify the thing, lifecycle qualifies it, owner and system
  // say who it belongs to and what it is part of. This list is the single home for these facts — the
  // header's tag row and meta strip are hidden in design-system.css rather than
  // repeating them in three places.
  const details: Array<[string, React.ReactNode]> = [];
  details.push(['Kind', entity.kind]);
  if (spec.type) details.push(['Type', String(spec.type)]);
  if (spec.lifecycle) details.push(['Lifecycle', String(spec.lifecycle)]);
  if (owner)
    details.push([
      'Owner',
      <Link to={entityPath(String(spec.owner), 'group')}>{owner}</Link>,
    ]);
  if (system)
    details.push([
      'System',
      <Link to={entityPath(String(spec.system), 'system')}>{system}</Link>,
    ]);
  if (md.namespace && md.namespace !== 'default')
    details.push(['Namespace', md.namespace]);

  return (
    <Grid container spacing={3}>
      {/* ---------- Left: the reading path ---------- */}
      <Grid item xs={12} md={8}>
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <div className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Summary
            </div>
            <div className={classes.actions}>
              <Tooltip title="Refresh from source" placement="top">
                <span>
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    disabled={refreshing}
                    onClick={onRefresh}
                    aria-label="Refresh entity"
                  >
                    <CachedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {editUrl && (
                <Tooltip title="Edit entity YAML" placement="top">
                  <IconButton
                    size="small"
                    className={classes.actionBtn}
                    href={editUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Edit entity"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
          <div className={classes.panel}>
            <Typography
              className={description ? classes.description : classes.muted}
            >
              {description || 'No description. Add one in the entity YAML.'}
            </Typography>
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>
            Readiness — {met} of {readiness.length}
          </div>
          <div className={classes.panel}>
            {readiness.map(r => (
              <Check key={r.label} ok={r.ok} label={r.label} />
            ))}
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Relationships</div>
          <div className={classes.panel}>
            {relRows.length === 0 ? (
              <Typography className={classes.muted}>
                Nothing linked yet. Relationships come from `spec.dependsOn`,
                `spec.system`, `spec.providesApis` and friends.
              </Typography>
            ) : (
              <Table size="small" className={classes.relTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Relation</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Lifecycle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {relRows.map(({ relation, ref }) => {
                    const e = related.get(ref);
                    const espec = (e?.spec ?? {}) as Record<string, unknown>;
                    return (
                      <TableRow key={`${relation}:${ref}`}>
                        <TableCell>
                          <Link to={entityPath(ref)}>
                            {e?.metadata.title ?? shortRef(ref)}
                          </Link>
                        </TableCell>
                        <TableCell className={classes.relDim}>
                          {relation}
                        </TableCell>
                        <TableCell className={classes.relDim}>
                          {e ? e.kind : '—'}
                        </TableCell>
                        <TableCell className={classes.relDim}>
                          {typeof espec.type === 'string' ? espec.type : '—'}
                        </TableCell>
                        <TableCell className={classes.relDim}>
                          {typeof espec.lifecycle === 'string'
                            ? espec.lifecycle
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </Grid>

      {/* ---------- Right: scannable reference ---------- */}
      <Grid item xs={12} md={4}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Details</div>
          <div className={classes.panel}>
            {details.map(([k, v], i) => (
              <div
                key={k}
                className={`${classes.detailRow} ${
                  i === details.length - 1 && tags.length === 0
                    ? classes.detailRowLast
                    : ''
                }`}
              >
                <span className={classes.detailKey}>{k}</span>
                <span className={classes.detailVal}>{v}</span>
              </div>
            ))}
            {tags.length > 0 && (
              <div className={`${classes.detailRow} ${classes.detailRowLast}`}>
                <span className={classes.detailKey}>Tags</span>
                <span className={classes.tags}>
                  {tags.map(t => (
                    <Chip key={t} size="small" label={t} />
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.sectionTitle}>Links</div>
          <div className={classes.panel}>
            {sourceUrl && (
              <a
                className={classes.linkRow}
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                <CodeIcon fontSize="small" /> View source
              </a>
            )}
            {hasDocs && (
              <Link className={classes.linkRow} to="docs">
                <DescriptionIcon fontSize="small" /> Documentation
              </Link>
            )}
            {links.map(l => (
              <a
                key={l.url}
                className={classes.linkRow}
                href={l.url}
                target="_blank"
                rel="noreferrer"
              >
                <LaunchIcon fontSize="small" /> {l.title ?? l.url}
              </a>
            ))}
            {!sourceUrl && !hasDocs && links.length === 0 && (
              <Typography className={classes.muted}>No links yet.</Typography>
            )}
          </div>
        </div>
      </Grid>
    </Grid>
  );
};
